import { defineStore, storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { axiosClient } from "@/helpers/auth"
import { useGlobalStore } from "@/store/storeGlobal"
import { useNotificationsStore } from "@/store/notificationsStore"
// import { useTableDefinitionStore } from "@/store/Setup/tableDefinitionStore" // Merged into tableDefinitionsStore
import { useUploadStoreV2 } from '@/store/storeUploadV2.js';

export const useImportDefinitionStore = defineStore('importDefinitionStore', () =>
{
  const globalStore = useGlobalStore()
  const uploadStoreV2 = useUploadStoreV2()
  // const tableDefinitionStore = useTableDefinitionStore() // Merged into tableDefinitionsStore
  // const { tableDefinition } = storeToRefs(tableDefinitionStore) // Merged into tableDefinitionsStore
  const notificationsStore = useNotificationsStore()
  const debugPrefix = '[ImportDefinition]'

  // State
  const importDefinition = ref(null)
  const selectedImportDefinitionId = ref(null)
  const importTableName = ref("")
  const fileImportData = ref({})
  const importDefinitions = ref(null)
  const displayAddImportDialog = ref(false)
  const description = ref('')
  const formErrors = ref({
    description: false
  })

  // Computed property for selectedImportDefinition
  const selectedImportDefinition = computed(() =>
    importDefinitions.value?.find(
      def => def.importDefinitionId === selectedImportDefinitionId.value
    ) || null
  )

  // Methods
  async function fetchImportDefinition (importDefinitionId)
  {
    globalStore.debug(`${debugPrefix} fetchImportDefinition`, importDefinitionId)
    const params = { importDefinitionId }
    const response = await axiosClient.get('import-definition', { params })
    const importDef = response?.data
    importDefinition.value = importDef

    // Oppdater også importDefinitions array slik at selectedImportDefinition fungerer
    if (importDef && importDefinitions.value) {
      const existingIndex = importDefinitions.value.findIndex(def => def.importDefinitionId === importDefinitionId)
      if (existingIndex >= 0) {
        importDefinitions.value[existingIndex] = importDef
      } else {
        importDefinitions.value.push(importDef)
      }
    } else if (importDef) {
      importDefinitions.value = [importDef]
    }
  }

  function validateImportForm ()
  {
    formErrors.value.description = !description.value?.trim()
    return !formErrors.value.description
  }

  async function saveImportDefinition (customImportDef, uploadId)
  {
    const response = await axiosClient.post('import-definition', customImportDef)
    if (response?.data) {
      selectedImportDefinitionId.value = response.data
      if (uploadId) {
        await uploadStoreV2.updateImportDefinitionId(uploadId, selectedImportDefinitionId.value)
      }
      await fetchImportDefinitions(customImportDef.tableDefinitionId)
      return true
    }
    return false
  }

  async function createImportDefinition (importDefinitionData)
  {
    // Hvis importDefinitionData er bare et tall (tableDefinitionId), konverter til objekt
    if (typeof importDefinitionData === 'number') {
      if (!validateImportForm()) {
        notificationsStore.addError('Vennligst fyll ut både navn og type')
        return false
      }

      importDefinitionData = {
        description: description.value,
        tableDefinitionId: importDefinitionData
      }
    }

    if (!importDefinitionData.tableDefinitionId) {
      notificationsStore.addError('Ingen tabell er valgt')
      return false
    }

    globalStore.debug(`${debugPrefix} createImportDefinition: `, importDefinitionData)
    const response = await axiosClient.post('import-definition', importDefinitionData)
    if (response?.data) {
      selectedImportDefinitionId.value = response.data
      await fetchImportDefinitions(importDefinitionData.tableDefinitionId)
      return response.data // Returner importDefinitionId
    }
    return null
  }

  async function deleteImportDefinition (importDefinitionId)
  {
    const params = { importDefinitionId }
    const response = await axiosClient.delete('import-definition', { params })
    if (response) {
      selectedImportDefinitionId.value = null;
      notificationsStore.addSuccess('Import definisjon er slettet')
      return true
    }
    return false
  }

  async function fetchImportDefinitions (tableDefinitionId)
  {
    globalStore.debug(`${debugPrefix} fetchImportDefinitions for tableDefinitionId`, tableDefinitionId)
    const params = { tableDefinitionId }
    const response = await axiosClient.get('import-definitions', { params })
    importDefinitions.value = response?.data
  }

  function selectImportDefinition (importDefinitionId)
  {
    selectedImportDefinitionId.value = importDefinitionId
  }

  async function updateColumnDefinition (id, columnDefinition)
  {
    globalStore.debug(`${debugPrefix} updateColumnDefinition: `, columnDefinition)
    const payload = {
      Ordinal: columnDefinition.Ordinal,
      ColumnId: columnDefinition.ColumnId,
      TargetColumnName: columnDefinition.TargetColumnName,
      SourceColumnName: columnDefinition.SourceColumnName,
      NullValue: columnDefinition.NullValue,
      DateFormat: columnDefinition.DateFormat,
      IsKey: columnDefinition.IsKey,
      IsCommentColumn: columnDefinition.IsCommentColumn
    }
    const response = await axiosClient.put(`import-definition/${id}/column`, payload)
    const isUpdateSuccess = response?.status === 204;
    if (isUpdateSuccess) {
      notificationsStore.addSuccess('Kolonne oppdatert');
    }
    return isUpdateSuccess;
  }

  async function updateImportDefinitionSteps (stepsPayload)
  {
    globalStore.debug(`${debugPrefix} updateImportDefinitionSteps: `, stepsPayload)
    const response = await axiosClient.put(`import-definition/${stepsPayload.importDefinitionId}/steps`, {
      steps: stepsPayload.steps
    })
    const isUpdateSuccess = response?.status === 204;
    if (isUpdateSuccess) {
      notificationsStore.addSuccess('Import-steg oppdatert');
    }
    return isUpdateSuccess;
  }

  async function updateImportDefinitionFileMetadata (importDefinitionId, fileMetadata)
  {
    globalStore.debug(`${debugPrefix} updateImportDefinitionFileMetadata: `, { importDefinitionId, fileMetadata })
    const response = await axiosClient.put(`import-definition/${importDefinitionId}/file-metadata`, fileMetadata)
    const isUpdateSuccess = response?.status === 204;
    if (isUpdateSuccess) {
      notificationsStore.addSuccess('Filmetadata oppdatert');
    }
    return isUpdateSuccess;
  }

  return {
    // State
    importDefinition,
    selectedImportDefinitionId,
    importTableName,
    fileImportData,
    importDefinitions,
    selectedImportDefinition,
    displayAddImportDialog,
    description,
    formErrors,

    // Methods
    fetchImportDefinition,
    saveImportDefinition,
    createImportDefinition,
    deleteImportDefinition,
    fetchImportDefinitions,
    selectImportDefinition,
    updateColumnDefinition,
    updateImportDefinitionSteps,
    updateImportDefinitionFileMetadata,
    validateImportForm
  }
})
